/**
 * API helper for updating current user display name (firstName, lastName).
 * PUT /users/profile with both fields; empty lastName clears stored surname. Backend syncs to Supabase user_metadata.
 */

import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';

export type ApiClientFn = (endpoint: string, config?: unknown) => Promise<AxiosResponse>;

export interface UpdateDisplayNameParams {
  firstName: string;
  /** Use empty string to clear last name when display name is a single word. */
  lastName: string;
}

export interface UpdateDisplayNameResult {
  success: boolean;
  message?: string;
}

/**
 * Update current user's display name via PUT /users/profile.
 * Always sends firstName and lastName so a single-word name clears a stale lastName on the server.
 */
export async function updateDisplayName(
  apiClientFn: ApiClientFn,
  params: UpdateDisplayNameParams
): Promise<UpdateDisplayNameResult> {
  if (!apiClientFn) return { success: false, message: undefined };
  const data = { firstName: params.firstName, lastName: params.lastName };
  try {
    const response = await apiClientFn(API_ENDPOINTS.users.profile, {
      method: 'PUT',
      data,
    });
    const body = response.data as { success?: boolean; message?: string } | undefined;
    if (
      response.status >= 200 &&
      response.status < 300 &&
      body?.success !== false
    ) {
      return { success: true };
    }
    const message =
      typeof body?.message === 'string' ? body.message : undefined;
    return { success: false, message };
  } catch (err: unknown) {
    const body = (err as { response?: { data?: { message?: string } } })?.response?.data;
    const fromBody = typeof body?.message === 'string' ? body.message : undefined;
    if (fromBody) {
      return { success: false, message: fromBody };
    }
    const fromError = err instanceof Error ? err.message : undefined;
    if (
      fromError === 'No active session' ||
      fromError === 'Authentication token not available' ||
      fromError === 'AUTH_TIMEOUT'
    ) {
      return { success: false, message: fromError };
    }
    return { success: false, message: undefined };
  }
}
